import { Info, ShieldAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { myNav } from '@/components/px/nav'
import { selectActiveOrgId, selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { healthTone, isoDayLabel, phaseDisplayName } from '@/features/projects/format'
import {
  fetchMyClient,
  selectMyClient,
  selectMyClientBlocked,
  selectMyClientError,
  selectMyClientLoading,
} from '@/features/my-work/slices/myWorkSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * `/my/client` — the client landing, WIRED (PX-W6.3, AC-6): the CLIENT-side
 * projection the server returns for this membership side. The rows carry the
 * `ProjectSummaryRow` field set ONLY — commercial figures are absent by
 * construction, so none can reach the DOM (I-3 / I-6).
 *
 * A firm-side (or client-unbound) caller receives the typed 403
 * `project.client.landing.side` and sees the side-refusal state — never a
 * client chooser. An empty client portfolio is a 200 empty state.
 */
export default function ClientPortfolioPage() {
  const { t } = useTranslation('myWork')
  const dispatch = useAppDispatch()
  const role = useAppSelector(selectActiveRoleKey)
  const orgId = useAppSelector(selectActiveOrgId)
  const client = useAppSelector(selectMyClient)
  const blocked = useAppSelector(selectMyClientBlocked)
  const loading = useAppSelector(selectMyClientLoading)
  const error = useAppSelector(selectMyClientError)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchMyClient(orgId))
  }, [dispatch, orgId])

  const title = client !== null ? t('client.title', { client: client.client.name }) : t('client.loadingTitle')

  let body: React.ReactNode
  if (blocked !== null) {
    body = (
      <div
        role="alert"
        data-testid="client-landing-blocked"
        className="mx-auto max-w-xl rounded-lg border border-border bg-card px-6 py-8 text-center"
      >
        <ShieldAlert className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
        <h2 className="mt-3 font-serif text-xl font-semibold text-foreground">{t('client.blockedTitle')}</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">{t('client.blockedBody')}</p>
        <p className="mt-2 text-sm text-ink-soft">
          <span className="v-figure mr-1.5 font-semibold tabular-nums">{blocked.status}</span>
          {blocked.code !== undefined && <span className="mr-1.5 font-mono text-xs">{blocked.code}</span>}
          {blocked.message}
        </p>
        <Link
          to="/my/board"
          className="mt-5 inline-flex items-center rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          {t('client.back')}
        </Link>
      </div>
    )
  } else if (error !== null) {
    /* PX-W6.6 (AC-2): the SHARED error panel, carrying the mapped KEY — never
       `error.message`, which can be an untranslated EN sentence in the NL UI. */
    body = (
      <ErrorPanel
        error={asApiError(error)}
        onRetryClicked={() => orgId !== null && void dispatch(fetchMyClient(orgId))}
      />
    )
  } else if (client === null) {
    body = <LoadingPanel label={t('client.loading')} />
  } else {
    body = (
      <>
        <div className="mt-5 grid gap-3" data-testid="client-engagements">
          {client.projects.length === 0 ? (
            <EmptyPanel title={t('client.emptyTitle')} body={t('client.emptyBody')} />
          ) : (
            client.projects.map((row) => (
              <Link
                key={row.id}
                to={`/project/${row.id}`}
                className="block rounded-lg border border-border bg-card p-4 hover:border-ring"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif text-lg font-semibold text-foreground">{row.name}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {[row.office, row.vertical, row.odooVersion !== undefined ? `Odoo ${row.odooVersion}` : undefined]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusPill tone="neutral">{phaseDisplayName(row.currentPhase)}</StatusPill>
                    <StatusPill tone={healthTone(row.health)}>{row.health ?? '—'}</StatusPill>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-4 border-t border-hairline pt-3 sm:grid-cols-4">
                  <div>
                    <SectionLabel>{t('client.columns.phase')}</SectionLabel>
                    <dd className="mt-1 text-sm text-foreground">{phaseDisplayName(row.currentPhase)}</dd>
                  </div>
                  <div>
                    <SectionLabel>{t('client.columns.office')}</SectionLabel>
                    <dd className="mt-1 text-sm text-foreground">{row.office ?? '—'}</dd>
                  </div>
                  <div>
                    <SectionLabel>{t('client.columns.vertical')}</SectionLabel>
                    <dd className="mt-1 text-sm text-foreground">{row.vertical ?? '—'}</dd>
                  </div>
                  <div>
                    <SectionLabel>{t('client.columns.goLive')}</SectionLabel>
                    <dd className="v-figure mt-1 text-sm tabular-nums text-foreground">
                      {row.goLiveDate ? isoDayLabel(row.goLiveDate) : '—'}
                    </dd>
                  </div>
                </dl>
              </Link>
            ))
          )}
        </div>

        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('client.scopeLine')}
        </p>
      </>
    )
  }

  return (
    <AppShell
      moduleLabel={t('client.moduleLabel')}
      breadcrumb={title}
      contextPill={
        client !== null
          ? t('client.contextPill', { count: client.projects.length }).toUpperCase()
          : t('client.pillFallback')
      }
      nav={myNav(role)}
    >
      <div className="w-full">
        <div className="mb-3">
          <ConceptBadge />
        </div>
        {/* The heading renders in EVERY state — a landing always names itself. */}
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{title}</h1>
          {client !== null && (
            <p className="mt-1.5 text-sm text-muted-foreground">
              {client.projects.length > 1 ? t('client.lede') : t('client.ledeSingle')}
            </p>
          )}
        </header>
        {loading && client === null && blocked === null && (
          <p role="status" className="mb-4 text-sm text-muted-foreground">
            {t('client.loading')}
          </p>
        )}
        <div className="mt-5">{body}</div>
        <DensityNote>{t('client.densityNote')}</DensityNote>
      </div>
    </AppShell>
  )
}
